import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AvTooltip, isValidSeverity, SeverityLabels, SeverityPrimaryColors } from '@fabric-ui/components';
import { dotStyle, flex, getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { abbreviateNumber, noop } from '@fabric-ui/utils';
import { Box, debounce, Portal, SxProps, useTheme } from '@mui/material';
import { formatPercent } from '@utils/dashboardDataUtils';
import { Cell, Label, LabelList, Pie, PieChart, PieLabel, ResponsiveContainer, Sector, Tooltip, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { NO_VALUE } from '../../../views/CustomDashboards/constants';
import { getDyPerWidget, getHeightPerWidget } from '../../../views/CustomDashboards/CustomDashboardsUtils';
import { getCustomTickFormatter } from '../../../views/CustomDashboards/hooks';
import { Data, PieSubType, WidgetCategory } from '../../../views/CustomDashboards/types';
import { AxisTickType, LabelFormatterOption } from '../../../views/CustomDashboards/types/types';
import AvTable from '../../Table/AvTable';
import { CUSTOM_CHART_LABEL_MAX_WIDTH, PolarLabel } from '../CustomChartLabel';
import CustomTooltip from '../CustomTooltip';
import { ChartClickInteraction } from '../types';
import { calculateTextWidthCavnas } from '../utils';
import { generateTopItemsPlusOthers, getPieCustomLabelMx } from './utils';

interface InnerLabelProps {
  viewBox?: Record<string, any>;
  totals: number;
  showTotalText?: boolean;
}

const InnerLabel: React.FC<InnerLabelProps> = ({ viewBox = { cx: 0, cy: 0, innerRadius: 0 }, totals, showTotalText = true }) => {
  if (!totals) {
    return null;
  }

  const { cx, cy, innerRadius } = viewBox;
  return (
    <g>
      {showTotalText && (
        <text
          className="custom-chart-text"
          fontSize={Math.min(40, innerRadius / 3.5)}
          x={cx}
          y={cy - Math.min(45, innerRadius / 4)}
          textAnchor="middle"
          fill="var(--color-content-base-secondary)">
          TOTAL
        </text>
      )}
      <text
        x={cx}
        y={showTotalText ? cy + Math.min(50, innerRadius / 3) : cy}
        textAnchor="middle"
        fontSize={Math.min(50, innerRadius / 2)}
        className="custom-chart-text"
        fontWeight={600}
        fill="var(--color-content-base-primary)">
        {abbreviateNumber(totals)}
      </text>
    </g>
  );
};

const shareCell = (v, totals) => <Box sx={{ textAlign: 'end' }}>{formatPercent(v, totals)}</Box>;
const valCell = v => <Box sx={{ fontWeight: 600, textAlign: 'end' }}>{(+v)?.toLocaleString() || v}</Box>;

interface MetricCellProps {
  v: any;
  color: string;
  maxWidth?: number | string;
  textColor?: { color: (val) => string };
  labelFormatter?: LabelFormatterOption;
  enableResponsiveLegend?: boolean;
}

const ELLIPSIS_WITH_LINE_CLAMP = {
  whiteSpace: 'break-spaces',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
};

const MetricCell: React.FC<MetricCellProps> = ({ v, color, maxWidth = 250, textColor, labelFormatter, enableResponsiveLegend }) => {
  const customItem = getCustomTickFormatter({
    isVertical: false,
    labelFormattingOption: labelFormatter,
    sx: { color: undefined },
    itemMaxWidth: 120,
  });
  const allowTextWrap = maxWidth === '100%'; // Determine if text wrapping should be allowed

  const renderContent = () => {
    if (customItem) {
      return customItem(v);
    }

    if (enableResponsiveLegend) {
      // Custom dashboard behavior: always show tooltip, apply line-clamp styles
      return (
        <AvTooltip title={v} sx={textColor}>
          <Box
            sx={{
              ...(allowTextWrap && ELLIPSIS_WITH_LINE_CLAMP),
            }}>
            {v}
          </Box>
        </AvTooltip>
      );
    }

    return (
      <AvTooltip
        sx={{
          ...textColor,
          ...(allowTextWrap && { whiteSpace: 'normal', textOverflow: 'initial' }),
        }}>
        {v}
      </AvTooltip>
    );
  };

  return (
    <Box sx={{ ...flex.itemsCenter, gap: 2, maxWidth }}>
      <Box sx={{ ...flex.row, ...dotStyle(color), flexShrink: 0 }} />
      {renderContent()}
    </Box>
  );
};

const renderActiveShape = props => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, disable } = props;
  return (
    <g>
      <Sector
        style={{ cursor: disable ? 'default' : 'pointer' }}
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        onClick={props?.onClick}
      />
      <Sector
        style={{ cursor: 'pointer' }}
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius - 1}
        outerRadius={outerRadius * 1.1}
        fill={fill}
        onClick={props?.onClick}
      />
    </g>
  );
};

interface LegendProps {
  variant?: 'interactive' | 'static';
  metricNameFormatter?: (v) => void;
  metricValueFormatter?: (v) => void;
  maxMetricWidth?: number | string;
  hideLines?: boolean;
  showShare?: boolean;
  showValue?: boolean;
}

export interface AvPieProps {
  data?: Array<Record<string, unknown>>;
  valKey?: string;
  valLabel?: string;
  metricKey?: string;
  onSelect?: (v, widgetData: Array<Record<string, unknown>>) => void;
  selected?: { color: string; index: number; value: string }[];
  legendProps?: LegendProps;
  numOfItems?: number;
  responseTotals?: number;
  customPalette?: string[];
  sx?: SxProps;
  showTableSelection?: boolean;
  additionalHeadCells?: Record<string, unknown>[];
  customOthersHandler?: (counts?: object, baseObj?: object, data?: []) => void;
  tooltipFields?: string[];
  showTotals?: boolean;
  showTable?: boolean;
  showLabels?: boolean;
  type?: PieSubType;
  className?: string;
  clickInteractions?: ChartClickInteraction[];
  severitySetting?: (v) => string;
  labelFormatter?: LabelFormatterOption;
  getFormattedColorCell?: (v: number) => string | undefined;
  tooltipProps?: React.ComponentProps<typeof CustomTooltip>;
  tooltipContent?: TooltipProps<any, any>['content'];
  rechartsTooltipProps?: Omit<TooltipProps<ValueType, NameType>, 'content'>;
  tableSx?: SxProps;
  showDataLabels?: boolean;
  formattingRuleField?: string;
  minChartWidth?: number;
  usePortalForTooltip?: boolean;
  enableResponsiveLegend?: boolean;
  innerRadiusWidth?: string;
}

const AvPie: React.FC<AvPieProps> = ({
  data: d = [],
  valKey = 'count',
  valLabel = 'Value',
  metricKey = 'category',
  onSelect,
  selected,
  legendProps = { variant: 'interactive', showShare: false, showValue: true },
  numOfItems,
  responseTotals,
  customPalette,
  sx = {},
  showTableSelection = false,
  additionalHeadCells = [],
  customOthersHandler,
  tooltipFields = ['count', 'share'],
  showTotals = true,
  showTable = true,
  showLabels = false,
  labelFormatter,
  type,
  className,
  clickInteractions,
  severitySetting,
  getFormattedColorCell,
  tooltipProps,
  tooltipContent,
  rechartsTooltipProps,
  tableSx,
  showDataLabels,
  formattingRuleField = metricKey,
  minChartWidth = 212,
  usePortalForTooltip = false,
  enableResponsiveLegend,
  innerRadiusWidth = '60%',
}) => {
  const theme = useTheme();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const defaultColors = theme.palette.chartColors;

  // Helper to resolve CSS custom properties to actual color values
  const resolveColor = (color: string): string => {
    if (typeof window !== 'undefined' && color?.startsWith('var(--')) {
      const varName = color.match(/var\((--[^,)]+)/)?.[1];
      if (varName) {
        const computed = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
        if (computed) return computed;
      }
    }
    return color;
  };

  const chartColors = (customPalette || defaultColors).map(resolveColor);
  const [active, setActive] = useState<number[]>([]);
  const isStaticLegend = legendProps.variant === 'static';
  const tableData = numOfItems ? generateTopItemsPlusOthers(d, metricKey, numOfItems, customOthersHandler) : d;
  const totals = responseTotals || tableData?.reduce((acc, entry) => acc + entry[valKey], 0);
  const pieData = tableData.map(entry => ({ ...entry, share: formatPercent(entry[valKey], totals) })) || [];
  const [activeTooltipForClickInteractions, setActiveTooltipForClickInteractions] = useState<Record<string, unknown>>();
  const [temporaryClickedCell, setTemporaryClickedCell] = useState<Record<string, unknown>>();
  const pieRef = useRef<any>(undefined);

  const getSliceColor = useCallback(
    (entry: Record<string, Data>, index: number): string => {
      const valueForFormattedColorCell = entry[formattingRuleField];
      const valueForSeverityCheck = entry[metricKey];

      const formattedColor = getFormattedColorCell?.(valueForFormattedColorCell as number);
      if (formattedColor) {
        return formattedColor;
      }

      if (isValidSeverity(valueForSeverityCheck)) {
        return SeverityPrimaryColors[valueForSeverityCheck as SeverityLabels];
      }

      return chartColors[index % chartColors.length];
    },
    [chartColors, getFormattedColorCell, formattingRuleField, metricKey]
  );

  const extraHeadCells =
    additionalHeadCells.map(cell => ({ ...cell, formatter: cell.formatter || valCell, type: cell.type || 'number' })) || [];

  const headCells = [
    {
      id: metricKey,
      isKey: true,
      label: metricKey,
      formatter: (v, row) => {
        const formattedName = legendProps.metricNameFormatter?.(v) || v;
        return MetricCell({
          v: formattedName === null ? NO_VALUE : formattedName,
          color: getSliceColor(
            row,
            tableData.findIndex(dataRow => dataRow === row)
          ),
          maxWidth: legendProps.maxMetricWidth,
          textColor: { color: () => 'var(--color-content-base-secondary)' },
          labelFormatter,
          enableResponsiveLegend,
        });
      },
      disableSortBy: true,
    },
    {
      id: valKey,
      label: valLabel,
      hidden: !legendProps.showValue,
      type: 'number',
      formatter: v => (legendProps.metricValueFormatter ? valCell(legendProps.metricValueFormatter(v)) : valCell(v)),
      disableSortBy: true,
    },
    {
      id: 'share',
      label: 'Share',
      hidden: !legendProps.showShare,
      type: 'number',
      formatter: (v, row) => shareCell(row[valKey], totals),
      disableSortBy: true,
    },
    ...extraHeadCells,
  ];

  useEffect(() => {
    if (selected) {
      setActive(selected.map(v => v.index));
    }
  }, [selected]);

  const innerRadius = 50;
  const outerRadius = 70;

  const arrow = ({ pieRef, value, outerRadius, innerRadius }) => {
    const midAngle = 180.0 * (1 - totals / 100);
    if (pieRef.current?.container) {
      const { offsetWidth, offsetHeight } = pieRef.current.container;
      const cx = offsetWidth / 2;
      const cy = offsetHeight / 2;
      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);
      const my = cy + (outerRadius + 2) * sin;
      const mx = cx + (outerRadius + 2) * cos;
      const ox = cx + (innerRadius - 2) * cos;
      const oy = cy + (innerRadius - 2) * sin;
      return (
        <g>
          <line y1={oy} x1={ox} y2={my} x2={mx} stroke="white" strokeWidth={8} />
          <line y1={oy} x1={ox} y2={my} x2={mx} stroke={severitySetting?.(value)} strokeWidth={4} strokeLinecap="round" />
        </g>
      );
    }
    return undefined;
  };

  const handleSelection = (selectionIndex, entry) => {
    if (!active.length && clickInteractions) {
      const availableCellClickInteractions = clickInteractions.filter(v => v.isEnabledForCell(entry));
      if (availableCellClickInteractions.length === 1) {
        availableCellClickInteractions[0].onClick({
          d: entry,
          widgetData: pieData,
          index: selectionIndex,
          originalValue: entry.originalValue,
          headCellType: entry.headCellType,
        });
      } else if (availableCellClickInteractions.length) {
        setTemporaryClickedCell(entry);
      }
    } else {
      const isAlreadyActive = typeof active.find(v => v === selectionIndex) === 'number';
      const nextSelection = isAlreadyActive ? active.filter(v => v !== selectionIndex) : [...active, selectionIndex];
      setActive(nextSelection);

      onSelect?.(
        nextSelection.map(index => ({
          value: [pieData[index][metricKey].toString()],
          color: isValidSeverity(pieData[index][metricKey]) ? SeverityPrimaryColors[pieData[index][metricKey]] : chartColors[index],
          index,
          headCellType: pieData[index].headCellType,
          originalValue: pieData[index].originalValue,
        })),
        pieData
      );
    }
  };

  const tableStyle: SxProps = {
    ...flex.justifyBetweenCenter,
    overflowY: 'auto',
    flexShrink: 0,
    py: 1.5,
    height: '100%',
    ...(enableResponsiveLegend && { width: '60%' }),
    '.MuiTableRow-root': {
      cursor: 'default',
      '&.active-row': { outline: 'none' },
      '&.MuiTableRow-hover:hover': { backgroundColor: isStaticLegend ? 'transparent' : undefined },
      '.MuiTableCell-root:not(.MuiTableCell-head):not(.totals-row)': {
        borderBottomColor: isStaticLegend || legendProps.hideLines ? 'transparent' : undefined,
      },
      '&:last-of-type>.MuiTableCell-root': { borderBottomColor: legendProps.hideLines ? 'transparent' : undefined },
    },
    ...tableSx,
  };

  const RADIAN = Math.PI / 180;

  const getPieLabelTextX = ({ mx, isCosPositive, pieDimMaxWidth, payload, labelFormatter }) => {
    const textPos = mx - (isCosPositive ? Math.min(pieDimMaxWidth, calculateTextWidthCavnas({ text: payload[metricKey] })) : 0);
    if (labelFormatter === LabelFormatterOption.NONE) {
      return textPos;
    }
    if (labelFormatter === LabelFormatterOption.SOURCE_ICONS_WITH_NAMES) {
      if (isCosPositive) {
        return textPos;
      }
      return mx;
    }
    return isCosPositive ? mx - (payload[metricKey].length > 1 ? 45 : 30) : mx;
  };

  const showShowPieLabel = ({ percent, payload }) => percent > 0.05 && payload[metricKey];

  // eslint-disable-next-line react/no-unstable-nested-components
  const CustomPieChartLabel: React.FC<PieLabel & any> = ({ cx, cy, midAngle, outerRadius, fill, payload, value, percent, innerRadius }) => {
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const isCosPositive = cos >= 0;

    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;

    const sign = isCosPositive ? 1 : -1;
    const mx = cx + getPieCustomLabelMx({ sign, innerRadius, showValue: legendProps.showValue });
    const pieDimMaxWidth = Math.abs(mx - sx) - 30;

    const textAnchor = isCosPositive ? 'end' : 'start';

    const showSeperator = legendProps.showValue && legendProps.showShare;
    const valueTextWidthInPx = legendProps.showValue ? calculateTextWidthCavnas({ text: value.toLocaleString(), isBold: true }) : 0;

    const customLabelFormatter = getCustomTickFormatter({
      labelFormattingOption: labelFormatter,
      isVertical: false,
      sx: { color: undefined, mb: 20 },
      itemMaxWidth: 100,
    });
    const seperatorX = mx - sign * (valueTextWidthInPx + 8);
    const ySeperator = getDyPerWidget({ labelFormatter, widgetType: WidgetCategory.Pie, rotation: AxisTickType.Horizontal }) || 20;
    const height = getHeightPerWidget({ labelFormatter, widgetType: WidgetCategory.Pie, rotation: AxisTickType.Horizontal }) || 20;

    return showShowPieLabel({ percent, payload }) ? (
      <g>
        <path d={`M${sx},${sy}L${mx},${sy}`} stroke={fill} fill="none" strokeWidth={2} />
        <foreignObject
          x={getPieLabelTextX({ mx, isCosPositive, pieDimMaxWidth, payload, labelFormatter })}
          y={sy - ySeperator}
          style={{ fontSize: '12px', color: 'var(--color-content-base-secondary)' }}
          width={pieDimMaxWidth}
          height={height}>
          <AvTooltip>{customLabelFormatter ? customLabelFormatter(payload[metricKey]) : payload[metricKey]}</AvTooltip>
        </foreignObject>
        {legendProps.showValue && (
          <text
            style={{ fontWeight: 600, fontSize: '12px' }}
            x={mx}
            y={sy + 17}
            textAnchor={textAnchor}
            fill="var(--color-content-base-primary)">
            {value.toLocaleString()}
          </text>
        )}
        {showSeperator && (
          <line x1={seperatorX} y1={sy + 6} x2={seperatorX} y2={sy + 19} stroke="var(--color-border-base-primary)" strokeWidth={1.5} />
        )}
        {legendProps.showShare && (
          <text
            x={mx - sign * (legendProps.showValue ? valueTextWidthInPx + 16 : 0)}
            y={sy + 17}
            textAnchor={textAnchor}
            fill="var(--color-content-base-primary)"
            style={{ fontSize: '12px' }}>
            {(percent * 100).toFixed(1)}%
          </text>
        )}
      </g>
    ) : undefined;
  };

  const handleHover = React.useRef(debounce(setActiveTooltipForClickInteractions, 100));

  return (
    <Box
      onMouseLeave={() => {
        handleHover.current(undefined);
        setTemporaryClickedCell(undefined);
      }}
      onClick={({ target }) => {
        if (target instanceof Element && target.classList.contains('recharts-surface')) {
          setTemporaryClickedCell(undefined);
        }
      }}
      sx={{
        ...flex.itemsCenter,
        // display: 'grid',
        // gridTemplateColumns: '40% 60%',
        // gridTemplateRows: '1fr',
        height: '100%',
        overflow: 'hidden',
        svg: { height: 'inherit', width: 'inherit', pointerEvents: 'unset' },
        '.recharts-surface': { overflow: 'visible' },
        ...sx,
      }}>
      <ResponsiveContainer
        width={showTable && enableResponsiveLegend ? '40%' : '100%'}
        height={showLabels ? '75%' : '100%'}
        className={className}
        minWidth={minChartWidth}
        style={{ ...flex.center }}>
        <PieChart
          ref={pieRef}
          style={{ position: 'unset' }}
          margin={
            showDataLabels
              ? {
                  right: CUSTOM_CHART_LABEL_MAX_WIDTH,
                  left: CUSTOM_CHART_LABEL_MAX_WIDTH,
                  top: 24,
                  bottom: 24,
                }
              : {}
          }>
          <Pie
            minAngle={10}
            isAnimationActive={false}
            data={pieData}
            stroke="var(--color-surface-base-primary)"
            outerRadius="90%"
            innerRadius={type === PieSubType.Donut ? innerRadiusWidth : '0%'}
            dataKey={valKey}
            activeShape={renderActiveShape}
            activeIndex={active}
            startAngle={90}
            endAngle={-270}
            {...(type === PieSubType.PieWithNeedle && { startAngle: 0, endAngle: 180, innerRadius, outerRadius })}
            blendStroke={pieData.length === 1}
            {...(showLabels && { labelLine: false, label: <CustomPieChartLabel /> })}>
            {showTotals && (
              <Label position="center" content={<InnerLabel totals={totals} showTotalText={type !== PieSubType.PieWithNeedle} />} />
            )}
            {showDataLabels ? <LabelList content={PolarLabel} position="outside" /> : null}
            {pieData.map((entry, index) => {
              const formattedColorCell = getCssVarColorWithOpacity(
                getSliceColor(entry, index),
                active.includes(index) || active.length === 0 ? 1 : 0.15
              );

              const isActiveCell =
                entry[metricKey] === activeTooltipForClickInteractions?.[metricKey] ||
                entry[metricKey] === temporaryClickedCell?.[metricKey];
              return (
                <Cell
                  onMouseEnter={e => {
                    setMousePos({ x: e.clientX, y: e.clientY });
                    if (!temporaryClickedCell) {
                      handleHover.current({ ...entry, index });
                    }
                  }}
                  onMouseLeave={() => handleHover.current(undefined)}
                  onBlur={() => handleHover.current(undefined)}
                  cursor={entry.disable || type === PieSubType.PieWithNeedle ? 'default' : 'pointer'}
                  filter={isActiveCell && type !== PieSubType.PieWithNeedle ? `drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.60)` : undefined}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${entry.id}${index}`}
                  onClick={
                    (onSelect || clickInteractions?.some(({ isEnabledForCell }) => isEnabledForCell(entry))) && !entry.disable
                      ? () => handleSelection(index, entry)
                      : noop
                  }
                  fill={formattedColorCell}
                  aria-label={entry[metricKey]}
                  role="img"
                  className={isActiveCell ? 'active-pie-cell' : undefined}
                  data-pie-active-cell-index={index}
                />
              );
            })}
          </Pie>
          {type !== PieSubType.PieWithNeedle && (
            <Tooltip
              wrapperStyle={{ pointerEvents: 'auto', zIndex: 1 }}
              allowEscapeViewBox={{ x: true, y: true }}
              {...(clickInteractions?.some(v => v.isEnabledForCell(temporaryClickedCell))
                ? {
                    active: !!activeTooltipForClickInteractions || !!temporaryClickedCell,
                    trigger: temporaryClickedCell ? 'click' : 'hover',
                  }
                : {})}
              offset={30}
              // @ts-expect-error: Safe - props passed to content but not typed
              onMouseEnter={(e: Event) => {
                e.stopPropagation();
                handleHover.current.clear();
              }}
              onMouseLeave={(e: Event) => {
                e.stopPropagation();
                handleHover.current(undefined);
              }}
              onClick={(e: Event) => {
                e.stopPropagation();
                if (activeTooltipForClickInteractions) {
                  setTemporaryClickedCell(activeTooltipForClickInteractions);
                }
              }}
              content={
                tooltipContent ||
                // eslint-disable-next-line react/no-unstable-nested-components
                (props => {
                  if (!props.active || !props.payload || !props.payload?.length) {
                    return null;
                  }
                  const { payload } = props;
                  const tooltip = (
                    <CustomTooltip
                      payload={payload}
                      showTitleLegend
                      showContentLegend={false}
                      tooltipFields={tooltipFields}
                      metricKey={metricKey}
                      clickInteractions={active.length ? undefined : clickInteractions}
                      areClickOptionsVisible={!!temporaryClickedCell}
                      setAreClickOptionsVisible={setTemporaryClickedCell}
                      widgetData={pieData}
                      sx={{ pointerEvents: 'all' }}
                      {...tooltipProps}
                      titleFormatter={(metricKey, payload) => {
                        const value =
                          legendProps.metricNameFormatter?.(payload?.[0]?.payload[metricKey]) || payload?.[0]?.payload[metricKey];
                        return tooltipProps?.titleFormatter ? tooltipProps.titleFormatter(value, payload) : value;
                      }}
                    />
                  );

                  if (usePortalForTooltip) {
                    return (
                      <Portal>
                        <Box
                          sx={{
                            position: 'fixed',
                            top: mousePos.y + 10,
                            left: mousePos.x + 10,
                            zIndex: theme => theme.zIndex.tooltip,
                            pointerEvents: 'none',
                          }}>
                          {tooltip}
                        </Box>
                      </Portal>
                    );
                  }
                  return tooltip;
                })
              }
              {...rechartsTooltipProps}
            />
          )}
          {type === PieSubType.PieWithNeedle && arrow({ pieRef, outerRadius, innerRadius, value: totals })}
        </PieChart>
      </ResponsiveContainer>
      {showTable && (
        <Box sx={tableStyle}>
          <AvTable
            sx={{ flexShrink: 0, height: '100%' }}
            tableContainerSx={{ overflowX: 'hidden' }}
            headCells={headCells as any}
            hasHeaderFilters={false}
            rows={tableData || []}
            allowEdit={false}
            size="xSmall"
            isSelectionControlled
            enableSelectionColor={!isStaticLegend || showTableSelection}
            onActive={noop}
            showHeader={!isStaticLegend}
            selectedKeys={active.map(v => tableData?.[v]?.[metricKey])}
          />
        </Box>
      )}
    </Box>
  );
};

export default AvPie;
